// <algolia-collection-carousel> - a product carousel populated by browsing a
// specific Algolia collection, ordered by the index's own ranking rather than
// Shopify's manual collection sort. Built for blog articles (the collection is
// supplied per-article via a metafield, wired to the section's collection
// setting through a dynamic source), but works anywhere a collection can be
// given.
//
// Reuses the same product tile (algolia-product-tile.js) and scroll-slider as
// the Recommend widget, but hits the Search API (browse a collection) rather
// than the Recommend API: there's no seed product to recommend against here,
// we just want "the good products in this collection, better grouped than
// Shopify's manual sort".
//
// Personalization-ready but deliberately OFF: when the Algolia Personalization
// add-on is enabled AND marketing/personalization consent is granted, this is
// where you'd add enablePersonalization + a durable userToken to the query to
// re-rank per shopper. It's left off because analytics consent alone doesn't
// cover personalization (a per-user profile is a marketing/personalization
// purpose, not analytics) - so today this is the non-personalized "better
// grouping" baseline, and personalization is a later, consent-gated switch.
import { AlgoliaClient } from "./algolia-client.js";
import { renderProductTile } from "./algolia-product-tile.js";
import { d as addItemById, a as contexts } from "./cart-cce84c00.js";
import { E as EVENTS } from "./events-58bc9098.js";

// Below this many products the desktop 5.5-column layout can't fill without
// looking sparse (a handful of tiles floating in a wide row, no scroll), so
// the widget hides on desktop only - see the [data-few-products] CSS rule in
// the section. Tablet/mobile (narrower, fewer columns) still look fine, so
// they keep showing; a genuinely empty collection is hidden everywhere.
const HIDE_ON_DESKTOP_BELOW = 5;

class AlgoliaCollectionCarousel extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const eventOptions = { signal: this.controller.signal };

    const configEl = this.querySelector("[js-algolia-collection-carousel-config]");
    if (!configEl) return;
    this.config = JSON.parse(configEl.textContent);

    // No collection wired up (e.g. the article's metafield isn't set, or the
    // dynamic source isn't connected) or missing Algolia creds - nothing to
    // render.
    if (!this.config.appId || !this.config.searchKey || !this.config.indexName || !this.config.collectionHandle) {
      this.classList.add("hidden");
      return;
    }

    this.client = new AlgoliaClient({ appId: this.config.appId, searchKey: this.config.searchKey });
    this.bindQuickAddDelegate(eventOptions);
    this.fetchProducts();
  }

  async fetchProducts() {
    // Browse the collection (empty query) - Algolia returns products in the
    // index's own ranking order, which beats Shopify's manual collection sort.
    // Point indexName at a conversions replica (e.g.
    // shopify_products_conversions) for best-selling order. NOT _tags:"unlisted"
    // mirrors the collection grid so hidden products never surface here either.
    const handle = this.config.collectionHandle.replace(/"/g, '\\"');
    const filters = `collections:"${handle}" AND NOT _tags:"unlisted"`;

    try {
      const result = await this.client.query(this.config.indexName, {
        query: "",
        filters,
        distinct: true,
        hitsPerPage: this.config.maxProducts,
      });
      const hits = result?.hits || [];

      if (!hits.length) {
        // Empty collection - hide everywhere.
        this.classList.add("hidden");
        return;
      }

      this.hits = hits;
      this.renderTiles(hits);
      if (hits.length < HIDE_ON_DESKTOP_BELOW) {
        this.dataset.fewProducts = "true";
      }
      this.classList.remove("loading");
    } catch (e) {
      console.error("[algolia-collection-carousel] collection browse failed", e);
      this.classList.add("hidden");
    }
  }

  // Same clone-and-replace as algolia-recommend.js's renderTiles: the
  // scroll-slider caches its child references once in connectedCallback and
  // never re-queries them, so writing tiles into the live track via innerHTML
  // would leave those caches pointing at detached skeleton nodes (dead
  // scroll/nav). Cloning the slider root, filling the clone's track, then
  // swapping it in wholesale forces the slider to disconnect/reconnect and
  // re-initialise against the real tiles.
  renderTiles(hits) {
    const anchor = this.querySelector(".algolia-collection-carousel__slider-anchor");
    const oldRoot = anchor?.firstElementChild;
    if (!oldRoot) return;

    const newRoot = oldRoot.cloneNode(true);
    const track = newRoot.querySelector(".scroll-slider__slider");
    track.innerHTML = hits.map((hit, i) => renderProductTile(hit, this.config, i)).join("");
    oldRoot.replaceWith(newRoot);
  }

  // Quick-add delegate - adds to cart and opens the drawer, but publishes NO
  // Algolia event: these are collection-browse tiles, not search results or
  // recommendations, so there's no queryID/model to attribute a click or
  // conversion to. Deliberately not importing algolia-product-tile.js's
  // bindQuickAddDelegate (hardwired to the collection grid and its queryID)
  // nor algolia-recommend.js's (fires recommend events). Error handling mirrors
  // the Recommend widget's: addItemById resolves even on a 422, dispatching a
  // bubbling QUICK_ADD_ERROR the plain button has no wrapper to catch, so we
  // listen for it here to show a short inline failure label.
  bindQuickAddDelegate(eventOptions) {
    this.addEventListener(
      "click",
      (event) => {
        const button = event.target.closest("[js-algolia-quick-add]");
        if (!button || button.disabled) return;

        button.disabled = true;
        const originalLabel = button.textContent;
        button.textContent = "...";

        let addErrorMessage = null;
        const handleAddError = (errorEvent) => {
          addErrorMessage = errorEvent.detail?.errorMessage || "Couldn't add to cart";
        };
        button.addEventListener(EVENTS.QUICK_ADD_ERROR, handleAddError, { once: true });

        addItemById(button.dataset.variantId, 1, contexts.productItem, button)
          .then(() => {
            if (addErrorMessage) {
              console.error("[algolia-collection-carousel] add to cart failed:", addErrorMessage);
              return;
            }
            window.dispatchEvent(new CustomEvent(EVENTS.CART_DRAWER_OPENING));
          })
          .catch((e) => {
            console.error("[algolia-collection-carousel] add to cart failed", e);
          })
          .finally(() => {
            button.removeEventListener(EVENTS.QUICK_ADD_ERROR, handleAddError);
            button.disabled = false;
            if (addErrorMessage) {
              button.textContent = "Unavailable";
              setTimeout(() => {
                button.textContent = originalLabel;
              }, 2500);
              return;
            }
            button.textContent = originalLabel;
          });
      },
      eventOptions,
    );
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

if (!customElements.get("algolia-collection-carousel")) {
  customElements.define("algolia-collection-carousel", AlgoliaCollectionCarousel);
}